import { ApolloClient, createHttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import _ from 'lodash';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import moment from 'moment-timezone';
import React from 'react';
import MetaTags from 'react-meta-tags';
import { RouteComponentProps } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view';
import styled from 'styled-components';
import { space } from 'styled-system';

import PublicEnv from 'shared/utils/public-env';
import { getOrderForDelivery } from 'shared/graphql/order/queries';
import { updateOrderStatus } from 'shared/graphql/order/mutations';
import { Button } from 'shared/components';
import { Receipt as UntypedReceipt } from 'shared/components/receipt';
import { ModalContainer, ModalClose } from 'shared/modals';
import { formatAddressWithoutApt } from 'shared/helpers/address';
import { getCustomerNameForOrder } from 'shared/helpers/orders';
import { paymentMethodsToDisplayNames } from 'shared/constants';
import { mediaQueries } from 'shared/styles';
import type { UIState } from 'src/state/ui';
import type { GqlOrdersStatusUpdateInput, GqlQueryGetOrderForDeliveryArgs } from 'types/graphql';
import GMap from 'src/delivery/components/map';
import type { DeliveryMapProps } from 'src/delivery/components/map';

// Fix for issues with LogRocket and Apollo Client
const fetcher: { fetch?: typeof window.fetch } = {};
if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
  fetcher.fetch = (...args) => window.fetch(...args);
}

type OwnProps = RouteComponentProps<{ id: string; token: string }>;

// mobx-react 6's inject() doesn't type injected props as optional; standard typed-getter workaround.
type InjectedProps = {
  UI: UIState;
};

// Mirrors exactly what getOrderForDelivery/updateOrderStatus select, not the full Orders type.
// A few fields (credit, otherFees, bottleDepositTaxCents, medicalCard, autoConfirmed, autoClosed)
// are query-only, so this.order (assignable from either response) marks them key-optional too.
type DeliveryOrderLocation = {
  lat?: number | null;
  lng?: number | null;
  ln1?: string | null;
  ln2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  // Never selected (always key-absent); present so formatAddressWithoutApt's params accept this type.
  country?: string | null;
};

type DeliveryOrderDeliveryInfo = {
  address?: string | null;
  apartmentNum?: string | null;
  deliveryAddress?: string | null;
  deliveryOption?: boolean | null;
  lastSearchedAddress?: string | null;
  nonDeliveryAddress?: string | null;
  location?: DeliveryOrderLocation | null;
};

type DeliveryOrderDispensary = {
  _id?: string | null;
  name?: string | null;
  cName?: string | null;
  phone?: string | null;
  logoImage?: string | null;
  address?: string | null;
  timezone?: string | null;
  featureFlags?: { hideDeliveryEstimate?: boolean | null } | null;
};

type DeliveryOrderMedicalCard = {
  expirationDate?: string | null;
  number?: string | null;
  state?: string | null;
  photo?: string | null;
};

type DeliveryOrderCustomerProfile = {
  medicalCard?: DeliveryOrderMedicalCard | null;
  driversLicense?: string | null;
  photoId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  birthday?: string | null;
};

type DeliveryOrderCustomer = {
  _id?: string | null;
  emails?: Array<{ address?: string | null } | null> | null;
  profile?: DeliveryOrderCustomerProfile | null;
};

type DeliveryOrderGuestCustomer = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  birthMonth?: string | null;
  birthDay?: string | null;
  birthYear?: string | null;
};

type DeliveryOrderLineItem = {
  basePrice?: number | null;
  option?: string | null;
  price?: number | null;
  product?: {
    _id?: string | null;
    Name?: string | null;
    type?: string | null;
    Options?: Array<string | null> | null;
    brandName?: string | null;
    brand?: { name?: string | null } | null;
  } | null;
  quantity?: number | null;
};

export type DeliveryOrder = {
  coupon?: { _id: string } | null;
  createdAt?: string | null;
  credit?: number | null;
  durationEstimates?: {
    delivery?: { lowInMinutes?: number | null; highInMinutes?: number | null } | null;
    pickup?: { lowInMinutes?: number | null; highInMinutes?: number | null } | null;
  } | null;
  deliveryFee?: number | null;
  dispensary?: DeliveryOrderDispensary | null;
  id?: string | null;
  customer?: DeliveryOrderCustomer | null;
  guestCustomer?: DeliveryOrderGuestCustomer | null;
  medicalOrder?: boolean | null;
  medicalCard?: DeliveryOrderMedicalCard | null;
  otherFees?: number | null;
  orderId?: string | null;
  orders: DeliveryOrderLineItem[];
  specialInstructions?: string | null;
  status?: string | null;
  taxAmount?: number | null;
  cannabisTax?: number | null;
  bottleDepositTaxCents?: number | null;
  tipAmount?: number | null;
  paymentFee?: number | null;
  paymentMethod?: string | null;
  subtotal?: string | null;
  totalCost?: string | null;
  isPreviewOrder?: boolean | null;
  terminal?: string | null;
  autoConfirmed?: boolean | null;
  autoClosed?: boolean | null;
  deliveryInfo?: DeliveryOrderDeliveryInfo | null;
};

// GraphQL absence is JS `null`, never `undefined`, for an always-selected field. propertyName is
// the next field the original would have dereferenced -- preserves its exact crash message.
function assertPresent<T>(value: T | null | undefined, propertyName: string): asserts value is T {
  if (value === null || value === undefined) {
    throw new TypeError(`Cannot read properties of null (reading '${propertyName}')`);
  }
}

function isPaymentDisplayNameKey(key: string): key is keyof typeof paymentMethodsToDisplayNames {
  return key in paymentMethodsToDisplayNames;
}

// receipt.jsx's untyped destructured props infer as all-required; only order is passed here
// (apolloClient is mobx-react injected).
const Receipt = UntypedReceipt as React.ComponentType<{ order: DeliveryOrder }>;

@inject('UI')
@observer
export default class DeliveryPage extends React.Component<OwnProps & Partial<InjectedProps>> {
  @observable initialCenter: DeliveryMapProps['initialCenter'] | undefined;
  @observable order: DeliveryOrder | undefined;
  @observable receiptModalOpen = false;
  @observable saving = false;

  private _apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

  private get injected(): InjectedProps {
    return this.props as InjectedProps & OwnProps;
  }

  componentDidMount(): void {
    void this.fetchOrder();
  }

  get apolloClient(): ApolloClient<NormalizedCacheObject> {
    const { params } = this.props.match;

    if (this._apolloClient) {
      return this._apolloClient;
    }
    this._apolloClient = new ApolloClient({
      cache: new InMemoryCache(),
      link: createHttpLink({
        uri: PublicEnv.graphqlUri,
        headers: { temporaryAdminAccessToken: params.token },
        ...fetcher,
      }),
      name: 'Delivery Page',
    });
    return this._apolloClient;
  }

  fetchOrder = async (): Promise<void> => {
    const { UI } = this.injected;
    const { params } = this.props.match;

    try {
      const { data } = await this.apolloClient.query<
        { getOrderForDelivery: DeliveryOrder | null },
        GqlQueryGetOrderForDeliveryArgs
      >({
        query: getOrderForDelivery,
        variables: {
          id: params.id,
        },
      });

      const order = data.getOrderForDelivery;
      assertPresent(order, 'deliveryInfo');
      assertPresent(order.deliveryInfo, 'location');
      const initialCenter = _.pick(order.deliveryInfo.location, ['lat', 'lng']);
      this.order = order;
      this.initialCenter = initialCenter;
    } catch {
      UI.showErnie('We were unable to find this order. Please contact support.', 'danger');
    }
  };

  closeOrder = async (): Promise<void> => {
    const { UI } = this.injected;
    const { params } = this.props.match;
    this.saving = true;

    try {
      const { data } = await this.apolloClient.mutate<
        { updateOrderStatus: DeliveryOrder | null },
        { input: GqlOrdersStatusUpdateInput }
      >({
        mutation: updateOrderStatus,
        variables: {
          input: {
            id: params.id,
            status: 'closed',
            closedByDriver: true,
          },
        },
      });

      this.order = data?.updateOrderStatus ?? undefined;
      this.saving = false;
      scrollIntoView(document.getElementById('successBanner'), { time: 300 });
    } catch {
      UI.showErnie('We were unable to close this order. Please contact support or close it from the admin.', 'danger');
      this.saving = false;
    }
  };

  render(): JSX.Element | null {
    const { order } = this;

    if (!order) {
      return null;
    }

    let medicalCard: DeliveryOrderMedicalCard | null | undefined;
    if (order.medicalOrder) {
      medicalCard = order.medicalCard ?? order.customer?.profile?.medicalCard ?? null;
    }

    const { guestCustomer } = order;
    const customerName = getCustomerNameForOrder(order);
    // No guestCustomer means a registered customer; preserves the original's crash if
    // customer/profile are missing instead.
    let customerPhone: string | null | undefined;
    let customerBirthday: moment.Moment;
    if (guestCustomer) {
      customerPhone = guestCustomer.phone;
      customerBirthday = moment(
        `${guestCustomer.birthMonth}-${guestCustomer.birthDay}-${guestCustomer.birthYear}`,
        'M-D-YYYY'
      );
    } else {
      assertPresent(order.customer, 'profile');
      assertPresent(order.customer.profile, 'phone');
      customerPhone = order.customer.profile.phone;
      customerBirthday = moment(order.customer.profile.birthday, 'M-D-YYYY');
    }

    const paymentMethodDisplayName =
      order.paymentMethod != null && isPaymentDisplayNameKey(order.paymentMethod)
        ? paymentMethodsToDisplayNames[order.paymentMethod]
        : 'Cash';

    // First read is 'address', matching the original's exact crash point if deliveryInfo were null.
    assertPresent(order.deliveryInfo, 'address');
    const { deliveryInfo } = order;

    return (
      <DeliveryPageContainer>
        <MetaTags>
          <title>Delivery Driver Page</title>
          <meta name='description' content='Delivery driver page' />
        </MetaTags>
        <>
          {order.status === 'closed' && (
            <SuccessBanner id='successBanner'>
              <p>
                <b>This delivery is closed!</b>
              </p>
              <p>{formatClosedAt(order.dispensary)}</p>
            </SuccessBanner>
          )}
          <Header>
            <img src='/icons/dutchie-logo.svg' />
            <HeaderText>
              <p>Current Delivery</p>
              <p>{`Order #${order.orderId}`}</p>
            </HeaderText>
          </Header>
          {this.initialCenter && (
            <GMap initialCenter={this.initialCenter} width='100%' height='240px' tag={deliveryInfo.address} />
          )}
          <OrderInfo className='delivery-section'>
            <Section pt='0'>
              <SectionContent>
                <Label>Address:</Label>
                <Info>
                  <p>{deliveryInfo.address}</p>
                  {deliveryInfo.apartmentNum ? (
                    <p style={{ marginTop: '-1px' }}>Apt/Ste:&nbsp;{deliveryInfo.apartmentNum}</p>
                  ) : null}
                  <p>
                    <a
                      href={`https://www.google.com/maps?q=${encodeURIComponent(
                        // Untyped destructured params infer as required; spelling out each key
                        // keeps them honestly optional.
                        String(
                          formatAddressWithoutApt({
                            ln1: deliveryInfo.location?.ln1,
                            city: deliveryInfo.location?.city,
                            state: deliveryInfo.location?.state,
                            zipcode: deliveryInfo.location?.zipcode,
                            country: deliveryInfo.location?.country,
                          })
                        )
                      )}`}
                      // fixes a pre-existing typo ('norefferer'); no visible behavior change
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      Get directions
                    </a>
                  </p>
                </Info>
              </SectionContent>
            </Section>
            <Section>
              <SectionContent>
                <Label>Customer:</Label>
                <Info>
                  <p>{customerName}</p>
                  <p>
                    <a href={`tel:${customerPhone?.replace(/\D/g, '')}`}>Call customer</a>
                  </p>
                </Info>
              </SectionContent>
              <SectionContent mt='40px'>
                <Label>Date of Birth:</Label>
                <Info>
                  <p>{customerBirthday.format('MM/DD/YYYY')}</p>
                </Info>
              </SectionContent>
            </Section>
            {order.medicalOrder && medicalCard && (
              <Section>
                <SectionContent>
                  <Label>Medical Card:</Label>
                  <Info>
                    <p>{medicalCard.number}</p>
                    <p>{`Exp: ${medicalCard.expirationDate}`}</p>
                  </Info>
                </SectionContent>
                <SectionContent mt='40px'>
                  <Label>Date of Birth:</Label>
                  <Info>
                    <p>{customerBirthday.format('MM/DD/YYYY')}</p>
                  </Info>
                </SectionContent>
              </Section>
            )}
            <Section>
              <SectionContent>
                <Label>Order Total:</Label>
                <Info>
                  <p>${order.totalCost}</p>
                  <p>
                    <ReceiptLink href='#' onClick={() => (this.receiptModalOpen = true)}>
                      View receipt
                    </ReceiptLink>
                  </p>
                </Info>
              </SectionContent>
              <SectionContent mt='40px'>
                <Label>Payment:</Label>
                <Info>
                  <p>{paymentMethodDisplayName}</p>
                </Info>
              </SectionContent>
            </Section>
            {order.status !== 'closed' && (
              <StyledButton loading={this.saving} onClick={this.closeOrder}>
                Complete Order
              </StyledButton>
            )}
          </OrderInfo>
          <Footer>
            <p>Need Help?</p>
            <p>
              <a href={`tel:${order.dispensary?.phone?.replace(/\D/g, '')}`}>Contact Store</a>
            </p>
            <p>
              <a href='tel:8668388244'>Contact Support</a>
            </p>
          </Footer>
          <DutchieFooter>
            <span className='copyright'>&copy; {new Date().getFullYear()}</span>
          </DutchieFooter>
        </>
        {this.receiptModalOpen && (
          <ModalContainer isOpen onRequestClose={() => (this.receiptModalOpen = false)}>
            <ReceiptContainer>
              <Receipt order={order} />
            </ReceiptContainer>
            <ModalClose onClick={() => (this.receiptModalOpen = false)} />
          </ModalContainer>
        )}
      </DeliveryPageContainer>
    );
  }
}

// dispensary can be missing (preserves the crash); a missing timezone *value* falls back to UTC
// (moment.tz() with no zone, verified) -- not a crash, so no throw for that case.
function formatClosedAt(dispensary: DeliveryOrderDispensary | null | undefined): string {
  assertPresent(dispensary, 'timezone');
  return (dispensary.timezone ? moment.tz(dispensary.timezone) : moment.tz()).format('LLL');
}

const DeliveryPageContainer = styled.div`
  height: 100vh;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  text-align: center;
`;

const SuccessBanner = styled.div`
  background-color: #669f77;
  color: #fff;
  font-size: 19px;
  padding: 17px;
  text-align: center;

  p:not(:last-child) {
    margin-bottom: 7.5px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 42.5px 41.5px 29.5px;

  img {
    height: 70px;
  }
`;

const HeaderText = styled.div`
  text-align: right;
  p:first-of-type {
    color: #46494c;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 6px;
    margin-top: 12.5px;
  }

  p:last-of-type {
    font-size: 34px;
    font-weight: 300;
  }
`;

const OrderInfo = styled.div`
  color: #46494c;
  padding: 42.5px 41.5px 29.5px;
`;

// space supports pt/mt at runtime (styled-system); styled.div's own types don't infer that.
const Section = styled.div<{ pt?: string }>`
  font-size: 18px;
  text-align: left;
  padding: 27px 0;
  border-bottom: 1px solid #d3dadf;
  ${space}
`;

const SectionContent = styled.div<{ mt?: string }>`
  display: flex;
  ${space}
`;

const Label = styled.div`
  width: 172.5px;
  font-weight: bold;
`;

const Info = styled.div`
  width: 225px;
  line-height: 31px;

  p + p {
    margin-top: 10px;
  }
`;

// anchor-is-valid only checks the literal `a` tag; aliasing it (established repo pattern) avoids
// this false positive.
const ReceiptLink = styled.a``;

const StyledButton = styled(Button)`
  width: 100%;
  height: 54px;
  margin-top: 45px;
  margin-bottom: 45px;
  font-size: 18px;
`;

const Footer = styled.div`
  background-color: #ecf0f3;
  border-top: none;
  color: #6d747b;
  font-size: 21px;
  padding-bottom: 47.5px;
  padding-top: 43px;
  text-align: center;
  font-size: 18px;

  p:not(:last-child) {
    margin-bottom: 25px;
  }
`;

const DutchieFooter = styled.div`
  background-color: #dfe7ec;
  color: #6a6f73;
  font-size: 18px;
  line-height: 52.5px;
  text-align: center;
`;

const ReceiptContainer = styled.div`
  overflow: auto;

  @media ${mediaQueries.tablet} {
    overflow: unset;
  }
`;
